import { format } from 'date-fns';
import {
  AtualizacaoVitrineInput,
  ContextoVitrine,
  FormularioVitrine,
  ParametrosPreview,
  VitrineRecomendacao,
} from './types';

/** Títulos que o app usa quando o campo fica vazio. */
export const TITULO_PADRAO: Record<ContextoVitrine, string> = {
  pdp: 'Quem comprou, também levou',
  sacola: 'Aproveite e leve também',
};

export type SituacaoVitrine = 'no-ar' | 'desligada' | 'fora-da-agenda';

export const situacaoDaVitrine = ({
  active,
  visible_now,
}: Pick<VitrineRecomendacao, 'active' | 'visible_now'>): SituacaoVitrine => {
  if (!active) return 'desligada';
  return visible_now ? 'no-ar' : 'fora-da-agenda';
};

export const formatarDataHora = (data?: string | Date | null): string =>
  data ? format(new Date(data), 'dd/MM/yyyy HH:mm') : '-';

/** Converte o que veio da query nos valores iniciais do formulário. */
export const vitrineParaFormulario = (
  vitrine: VitrineRecomendacao,
): FormularioVitrine => ({
  active: vitrine.active,
  title: vitrine.title ?? '',
  subtitle: vitrine.subtitle ?? '',
  size: vitrine.size,
  min_products: vitrine.min_products,
  position: vitrine.position,
  rank_by: vitrine.rank_by,
  // a PDP tem um produto só: o modo é sempre "lista única"
  mode: vitrine.context === 'pdp' ? 'any' : vitrine.mode,
  min_cooccurrence: vitrine.min_cooccurrence,
  months: vitrine.months,
  limit: vitrine.limit,
  schedule_start: vitrine.schedule_start
    ? new Date(vitrine.schedule_start)
    : null,
  schedule_end: vitrine.schedule_end ? new Date(vitrine.schedule_end) : null,
});

/**
 * Monta o input da mutation só com os campos do formulário, com os números
 * convertidos. A posição só vale na PDP e o modo só é editável na sacola.
 * Título/subtítulo vazios voltam ao texto padrão do app (o cms-api grava null).
 */
export const formularioParaInput = (
  valores: FormularioVitrine,
  contexto: ContextoVitrine,
): AtualizacaoVitrineInput => ({
  active: valores.active,
  title: valores.title.trim(),
  subtitle: valores.subtitle.trim(),
  size: Number(valores.size),
  min_products: Number(valores.min_products),
  ...(contexto === 'pdp'
    ? { position: valores.position }
    : { mode: valores.mode }),
  rank_by: valores.rank_by,
  min_cooccurrence: Number(valores.min_cooccurrence),
  months: Number(valores.months),
  limit: Number(valores.limit),
  ...(valores.schedule_start && { schedule_start: valores.schedule_start }),
  // null remove a data de fim
  schedule_end: valores.schedule_end ?? null,
});

/** Parâmetros do algoritmo (ainda não salvos) para a pré-visualização. */
export const formularioParaParametros = (
  valores: FormularioVitrine,
  contexto: ContextoVitrine,
): ParametrosPreview => ({
  rankBy: valores.rank_by,
  mode: contexto === 'pdp' ? 'any' : valores.mode,
  minCooccurrence: Number(valores.min_cooccurrence),
  months: Number(valores.months),
  limit: Number(valores.limit),
});

/** Lê os productIds digitados (separados por vírgula, espaço ou quebra de linha). */
export const lerProductIds = (texto: string): string[] =>
  Array.from(
    new Set(
      texto
        .split(/[\s,;]+/)
        .map(id => id.trim())
        .filter(Boolean),
    ),
  );

export const formatarPercentual = (valor: number): string =>
  `${(valor * 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;

export const formatarDecimal = (valor: number): string =>
  valor.toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

export const formatarInteiro = (valor: number): string =>
  valor.toLocaleString('pt-BR');
